import React from 'react';
import TabContext from '@mui/lab/TabContext';
import assistantClient from '@requests/telnyx/assistants.requests';

import { useSelector } from 'react-redux';
import { TabList, TabPanel } from '@mui/lab';
import { Alert, Tab } from '@mui/material';
import { ArticleOutlined, LoopOutlined, SecurityOutlined } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { selectCommandByNodeId, State } from '@store/index';
import { NodeInputExpression, NodeSidebar, NodeSidebarTab, NodeSidebarTabsContainer } from '@common/Node/NodeSidebar';
import { NodeData } from '@common/Node/NodeSidebar/NodeData';
import { AssistantSelector } from '@common/Node/NodeSidebar/Custom/AssistantSelector';
import { AIAssistant } from '@requests/telnyx';
import { RequestError } from '@requests/common';
import { AssistantNodeState } from '../../types';
import { isPaidModel, isToolModel } from '@common/Autocomplete/Base';
import { getModelKey } from '@common/NodeAuth/ModelAuthKey';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@common/Button';

interface Props {
  tab: string;
  setTab: React.Dispatch<string>;
}

export const AssistantTabs: React.FC<Props> = ({ tab, setTab }) => {
  const navigate = useNavigate();

  const { service_id } = useParams();

  const themeType = useSelector((state: State) => state.flow.theme);
  const authentication = useSelector((state: State) => state.tabs.active.flow?.authentication);

  const { data: assistants = [] } = useQuery<AIAssistant[], RequestError>({
    queryKey: ['ai_assistants'],
    queryFn: assistantClient.list,
    retry: false,
  });

  const command = useSelector((state: State) => selectCommandByNodeId(state, state.tabs.active.selectedNode));
  if (!command) return;

  const state = command.state as AssistantNodeState;
  const assistant = assistants.find((assistant) => assistant.id === state.assistant_id);

  const model = assistant?.model || '';
  const modelKey = getModelKey(model);
  const secretName = modelKey ? authentication?.[modelKey] : null;

  const fields = command.fields;
  const background = themeType === 'dark' ? '#7272fa' : command.config.background;

  const nameField = fields.find((field) => field.id === 'name');
  const queryField = fields.find((field) => field.id === 'query');
  const sessionIdField = fields.find((field) => field.id === 'session_id');
  const includeSessionToolsField = fields.find((field) => field.id === 'include_session_tools');
  const filesField = fields.find((field) => field.id === 'files');
  const senderIdField = fields.find((field) => field.id === 'sender_id');
  const assistantIdField = fields.find((field) => field.id === 'assistant_id');

  const openAssistant = () => {
    navigate(`/${service_id}/assistants/${assistant?.id}`);
  };

  return (
    <NodeSidebar>
      <TabContext value={tab}>
        <NodeSidebarTabsContainer color={background}>
          <TabList onChange={(_, newTab) => setTab(newTab)}>
            <Tab disableRipple label='Node' value='1' />
            <Tab title='Data' disableRipple iconPosition='start' icon={<ArticleOutlined />} value='2' />
            <Tab
              title='Auth'
              disabled={!isPaidModel(model)}
              disableRipple
              iconPosition='start'
              icon={<SecurityOutlined />}
              value='3'
            />
          </TabList>
        </NodeSidebarTabsContainer>
        <TabPanel value='1'>
          <NodeSidebarTab>
            {nameField && <NodeInputExpression field={nameField} command={command} />}
            {assistantIdField && <AssistantSelector field={assistantIdField} command={command} single />}
            {queryField && <NodeInputExpression field={queryField} command={command} />}
            {filesField && <NodeInputExpression field={filesField} command={command} />}
            {sessionIdField && <NodeInputExpression field={sessionIdField} command={command} />}
            {includeSessionToolsField && state?.session_id?.value && isToolModel(model) && (
              <NodeInputExpression field={includeSessionToolsField} command={command} />
            )}
            {senderIdField && state?.session_id?.value && (
              <NodeInputExpression field={senderIdField} command={command} />
            )}
          </NodeSidebarTab>
        </TabPanel>
        <TabPanel value='2'>
          <NodeData command={command} />
        </TabPanel>
        <TabPanel value='3'>
          <NodeSidebarTab>
            {assistant?.llm_api_key_ref ? (
              <Alert severity='warning'>
                This method of setting the API key is deprecated. Please update the assistant configuration directly to
                change your assistant API key.
              </Alert>
            ) : secretName ? (
              <Alert severity='warning'>
                {`The API key for AI Assistants is now defined in the assistant configuration. Your previously set API key (${secretName}) is still saved and will continue to work. To change the API key, please update the assistant configuration.`}
              </Alert>
            ) : (
              <Alert severity='error'>
                {`The API key for AI Assistants is now defined in the assistant configuration. This assistant does not have an API key defined. Please update the assistant to include a valid API key.`}
              </Alert>
            )}
            <Button fullWidth onClick={openAssistant} startIcon={<LoopOutlined />}>
              Change Assistant API Key
            </Button>
          </NodeSidebarTab>
        </TabPanel>
      </TabContext>
    </NodeSidebar>
  );
};
